import { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { useMutation, useQuery } from "convex/react";
import { chatsStore, useChatsStore } from "../stores/chatsStore";
import type { Chat, ChatAgent } from "../types";
import { api } from "../lib/convexApi";

const AGENT_LABELS: Record<ChatAgent, string> = {
  claude: "Claude Code",
  codex: "Codex",
  cursor: "Cursor",
  gemini: "Gemini",
  copilot: "GitHub Copilot",
  opencode: "opencode",
};

const AGENT_BADGE_COLOR: Record<ChatAgent, string> = {
  claude: "var(--color-agent-claude, #C77B58)",
  codex: "var(--color-agent-codex, #3FA47C)",
  cursor: "var(--color-agent-cursor, #6366F1)",
  gemini: "var(--color-agent-gemini, #4285F4)",
  copilot: "var(--color-agent-copilot, #0969DA)",
  opencode: "var(--color-agent-opencode, #888888)",
};

function groupByRecency(chats: Chat[]): Array<{ label: string; chats: Chat[] }> {
  const now = Date.now();
  const today = now - 24 * 3600_000;
  const yesterday = today - 24 * 3600_000;
  const week = now - 7 * 24 * 3600_000;
  const groups: Record<string, Chat[]> = {
    Today: [],
    Yesterday: [],
    "This week": [],
    Older: [],
  };
  for (const c of [...chats].sort((a, b) => b.lastActivity - a.lastActivity)) {
    if (c.lastActivity >= today) groups.Today.push(c);
    else if (c.lastActivity >= yesterday) groups.Yesterday.push(c);
    else if (c.lastActivity >= week) groups["This week"].push(c);
    else groups.Older.push(c);
  }
  return Object.entries(groups)
    .filter(([, cs]) => cs.length)
    .map(([label, cs]) => ({ label, chats: cs }));
}

interface Props {
  workbenchId: string;
  onSelect?: (chatId: string) => void;
}

export function ChatList({ workbenchId, onSelect }: Props) {
  const allChats = useChatsStore((s) => s.chats);
  const activeId = useChatsStore((s) => s.activeId);
  const unread = useChatsStore((s) => s.unread);
  const chats = allChats.filter((c) => c.workbenchId === workbenchId);
  const [newOpen, setNewOpen] = useState(false);

  // Reactive Convex subscription. When Convex returns a list, write it
  // through to the zustand store so all downstream consumers (ChatPanel,
  // ChatList rows) re-render with the latest server-truth. Skip the
  // legacy chatApi fallback when Convex is configured — useQuery returns
  // undefined while loading, an array (possibly empty) when ready.
  // Per [[feedback-pr47-second-pass-patterns]] #24, mountedRef gates the
  // setState path so a unmount-then-resolve order doesn't fire on a
  // stale component.
  // Spec 5.1 PR 6 — pass workbenchId so the Convex list filters server-side
  // (matches the legacy /api/chats?workbenchId=… query string surface).
  // The downstream filter `chats.filter(c => c.workbenchId === workbenchId)`
  // remains a defense-in-depth check for the local-store rows that may
  // still carry the prior-workbench assignment from C58-5.
  const convexChats = useQuery(api.chats.queries.list, { workbenchId });
  // Spec 5.1 PR 6 — convex:api.chats.mutations.create replaces the
  // legacy POST /api/chats. The mutation returns the new chat's
  // Convex Id which the React layer treats as the chat id.
  const createChatMut = useMutation(api.chats.mutations.create);
  // Soft-deletes the chat (chats.deleteChat sets deleted:true); the reactive
  // list query filters deleted rows, so the row disappears on the next tick.
  // This is the "delete" half of "saved until deleted" — there was no delete
  // affordance before.
  const deleteChatMut = useMutation(api.chats.mutations.deleteChat);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!mountedRef.current) return;
    if (convexChats === undefined) {
      // Loading — keep the existing store contents but flag spinner.
      chatsStore.setLoading(true);
      return;
    }
    chatsStore.setLoading(false);
    // Map Convex row shape onto the legacy Chat shape so the existing
    // renderer keeps working. Per [[feedback-pr46-review-patterns]] #5
    // we consume the actual Convex return shape (_id, agentName, title,
    // lastMessageAt, createdAt) without stale wrappers.
    // Upsert each Convex chat into the store, PRESERVING the messages +
    // approvals + resumeHandle that the local store already has for
    // that chat id. Wholesale `setChats(mapped)` would zero out
    // `messages` for every chat on every reactive update — which
    // wiped in-flight SSE traffic for active conversations (Gemini
    // PR #58 finding).
    const existing = chatsStore.getSnapshot().chats;
    const byId = new Map(existing.map((c) => [c.id, c]));
    const mapped: Chat[] = (convexChats as any[]).map((c) => {
      const id = c._id as string;
      const prior = byId.get(id);
      // Preserve the chat's prior workbench assignment instead of
      // force-assigning the currently-rendered workbench (Codex PR #58
      // C58-5). Force-assignment caused every chat to reclassify into
      // whichever workbench tab was active, breaking isolation. New
      // chats (no prior entry) default to the active workbench, which
      // matches the legacy "create-in-current-tab" semantic. Long term
      // the Convex `chats` schema should carry workbenchId itself
      // (tracked for a follow-up PR), at which point this falls back to
      // c.workbenchId.
      const chatWorkbenchId = prior?.workbenchId ?? workbenchId;
      return {
        id,
        workbenchId: chatWorkbenchId,
        agent: (c.agentName as ChatAgent) || "claude",
        workspaceDir: prior?.workspaceDir ?? "/tmp",
        title: (c.title as string) || prior?.title || "Untitled chat",
        createdAt: (c.createdAt as number) ?? prior?.createdAt ?? 0,
        lastActivity:
          (c.lastMessageAt as number) ?? (c.createdAt as number) ?? prior?.lastActivity ?? 0,
        messages: prior?.messages ?? [],
        approvals: prior?.approvals ?? [],
        resumeHandle: prior?.resumeHandle ?? null,
      };
    });
    chatsStore.setChats(mapped);
  }, [convexChats, workbenchId]);

  // Spec 5.1 PR 6 — the legacy chatApi.list() REST fallback was
  // removed because the /api/chats GET route is 410 from this PR
  // forward. Convex is the canonical source; the existing
  // chatsStore.setLoading(true) call above already covers the
  // loading state until useQuery resolves.

  async function createChat(agent: ChatAgent) {
    setNewOpen(false);
    try {
      // Spec 5.1 PR 6 — Convex mutation replaces POST /api/chats.
      // agentName matches the legacy "agent" body key by way of the
      // chats schema's agentName field. workspaceDir is not currently
      // captured in the UI's "New chat" flow — the legacy server also
      // accepted no workspaceDir when none was provided and the chat
      // fell back to a default downstream.
      const newId = (await createChatMut({
        agentName: agent,
        workbenchId,
      })) as string;
      // Synthesize a local Chat row that matches what the reactive
      // useQuery will return on its next tick. Without this, the UI
      // wouldn't have any row to set active until the Convex
      // subscription resolves (which happens on the next round-trip).
      const optimistic: Chat = {
        id: newId,
        workbenchId,
        agent,
        workspaceDir: "/tmp",
        title: "New chat",
        createdAt: Date.now(),
        lastActivity: Date.now(),
        messages: [],
        approvals: [],
        resumeHandle: null,
      };
      chatsStore.upsert(optimistic);
      chatsStore.openChat(newId);
      onSelect?.(newId);
    } catch (err) {
      chatsStore.setError(err instanceof Error ? err.message : "Could not create chat");
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm("Delete this chat? Its history will be removed.")) return;
    try {
      await deleteChatMut({ id: id as never });
      // Drop the deleted chat from the open-pane grid (openChats) and refocus
      // the last remaining pane. setActive(null) alone left the deleted chat in
      // openChats, so EditorFrame kept rendering a stuck "Pick a chat…"
      // placeholder pane for it. closeChat removes it from the grid and
      // refocuses; the reactive list query drops it from `chats` on the next
      // tick. (M1)
      chatsStore.closeChat(id);
    } catch (err) {
      chatsStore.setError(err instanceof Error ? err.message : "Could not delete chat");
    }
  }

  const grouped = groupByRecency(chats);

  return (
    <div className="chat-list">
      <div className="chat-list__header">
        <button
          type="button"
          className="chat-list__new"
          aria-label="New chat"
          onClick={() => setNewOpen((v) => !v)}
        >
          <Plus size={14} /> New chat
        </button>
        {newOpen && (
          <div className="chat-list__agent-menu" role="menu">
            {(Object.keys(AGENT_LABELS) as ChatAgent[]).map((agent) => (
              <button key={agent} role="menuitem" type="button" onClick={() => createChat(agent)}>
                <span className="agent-badge" style={{ background: AGENT_BADGE_COLOR[agent] }}>
                  {agent.slice(0, 2).toUpperCase()}
                </span>
                {AGENT_LABELS[agent]}
              </button>
            ))}
          </div>
        )}
      </div>

      {chats.length === 0 ? (
        <p className="chat-list__empty">
          No chats yet — click &ldquo;New chat&rdquo; to start one.
        </p>
      ) : (
        grouped.map(({ label, chats: rows }) => (
          <div key={label} className="chat-list__group">
            <div className="chat-list__group-label">{label}</div>
            {rows.map((chat) => (
              // Row is a div role="tab" (not a <button>) so the delete control
              // can be a real nested <button> — interactive content inside a
              // <button> is invalid HTML and was keyboard-inaccessible.
              <div
                key={chat.id}
                role="tab"
                tabIndex={0}
                aria-selected={chat.id === activeId}
                className={`chat-list__row${chat.id === activeId ? " is-active" : ""}`}
                onClick={() => {
                  chatsStore.openChat(chat.id);
                  onSelect?.(chat.id);
                }}
                onKeyDown={(event) => {
                  // Only the row itself — not Enter/Space bubbling up from the
                  // nested delete <button> (else we'd preventDefault its own
                  // keyboard activation and open the chat instead of deleting).
                  if (
                    event.target === event.currentTarget &&
                    (event.key === "Enter" || event.key === " ")
                  ) {
                    event.preventDefault();
                    chatsStore.openChat(chat.id);
                    onSelect?.(chat.id);
                  }
                }}
              >
                <span className="agent-badge" style={{ background: AGENT_BADGE_COLOR[chat.agent] }}>
                  {chat.agent.slice(0, 2).toUpperCase()}
                </span>
                <span className="chat-list__title">{chat.title}</span>
                {unread.has(chat.id) && <span className="chat-list__dot" aria-label="Unread" />}
                <button
                  type="button"
                  className="chat-list__delete"
                  aria-label="Delete chat"
                  title="Delete chat"
                  onClick={(event) => {
                    event.stopPropagation();
                    void handleDelete(chat.id);
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}
